import { useMemo, useState } from 'react'
import {
  Alert,
  AdvancedDisclosure,
  Button,
  Checkbox,
  Dialog,
  DecisionReview,
  Field,
  FormLayout,
  FormLayoutRow,
  Input,
  Radio,
  Select,
  StatusBadge,
  Stepper,
  Switch,
} from './common'
import { ASSIGNABLE_ROLES, ROLES, formatRoleLabel } from '../constants/roles'
import styles from './UserAccessDialog.module.css'

const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const STEPS = [
  { value: 'account', label: 'Account' },
  { value: 'access', label: 'Organization access' },
  { value: 'review', label: 'Review' },
]

const SCOPE_OPTIONS = [
  {
    value: 'selected',
    label: 'Selected organizations',
    description: 'Grant access only to the organizations selected below.',
  },
  {
    value: 'all_current',
    label: 'All current organizations',
    description: 'Grant access to every organization that exists now. New organizations are not included.',
  },
  {
    value: 'all',
    label: 'All current and future organizations',
    description: 'Grant access now and automatically include organizations created later.',
  },
]
const PRODUCT_ACTIONS = [
  { value: 'view', label: 'View' },
  { value: 'configure', label: 'Configure' },
  { value: 'publish', label: 'Publish' },
  { value: 'analyze', label: 'Analyze' },
  { value: 'operate', label: 'Operate' },
]

const ROLE_PRODUCT_ACTIONS = {
  [ROLES.VIEWER]: ['view'],
  [ROLES.EDITOR]: ['view', 'publish', 'analyze'],
  [ROLES.EXTERNAL_ORG_ADMIN]: ['view', 'configure', 'publish', 'analyze'],
  [ROLES.INTERNAL_ORG_ADMIN]: PRODUCT_ACTIONS.map((action) => action.value),
}

const productOverrideKey = (organizationId, productKey) => `${organizationId}|${productKey}`

function formatProductActions(actions) {
  const selectedActions = new Set(actions || [])
  const knownActions = PRODUCT_ACTIONS
    .filter((action) => selectedActions.has(action.value))
    .map((action) => action.label)
  const customActions = [...selectedActions]
    .filter((action) => !PRODUCT_ACTIONS.some((item) => item.value === action))
    .sort()
  const labels = [...knownActions, ...customActions]
  return labels.length ? labels.join(', ') : 'No access'
}

function productActionsMatch(left, right) {
  return [...new Set(left || [])].sort().join('|') === [...new Set(right || [])].sort().join('|')
}


function initialState(user) {
  const roles = Object.fromEntries((user?.organizations || []).map((organization) => [organization.id, organization.role]))
  const productOverrides = Object.fromEntries((user?.product_overrides || []).map((override) => [
    productOverrideKey(override.organization_id, override.product_key),
    override.actions,
  ]))
  return {
    email: user?.email || '',
    displayName: user?.display_name || '',
    scope: user?.scope || 'selected',
    defaultRole: user?.default_role || ROLES.INTERNAL_ORG_ADMIN,
    selectedOrganizationIds: new Set((user?.organizations || []).map((organization) => organization.id)),
    roles,
    commandCenterAccess: Boolean(user?.command_center_access),
    productOverrides,
  }
}

export function UserAccessDialog({ user, organizations = [], onClose, onSubmit, isSubmitting = false, error }) {
  const editing = Boolean(user)
  const baseline = useMemo(() => initialState(user), [user])
  const [step, setStep] = useState('account')
  const [form, setForm] = useState(() => initialState(user))
  const currentStepIndex = STEPS.findIndex((item) => item.value === step)
  const accountValid = editing || Boolean(EMAIL_ADDRESS_PATTERN.test(form.email.trim()) && form.displayName.trim())

  const selectedOrganizations = useMemo(() => {
    if (form.scope !== 'selected') return organizations
    return organizations.filter((organization) => form.selectedOrganizationIds.has(organization.id))
  }, [form.scope, form.selectedOrganizationIds, organizations])
  const commandCenterEligible = useMemo(() => selectedOrganizations.some((organization) => (
    (form.roles[organization.id] || form.defaultRole) === ROLES.INTERNAL_ORG_ADMIN
  )), [form.defaultRole, form.roles, selectedOrganizations])
  const accessValid = organizations.length > 0 && (form.scope !== 'selected' || selectedOrganizations.length > 0)
  const canContinue = step === 'account' ? accountValid : step === 'access' ? accessValid : true
  const selectedProductOverrides = useMemo(() => selectedOrganizations.flatMap((organization) => (
    (organization.products || [])
      .filter((product) => Object.hasOwn(form.productOverrides, productOverrideKey(organization.id, product.key)))
      .map((product) => ({
        organization,
        product,
        actions: form.productOverrides[productOverrideKey(organization.id, product.key)],
      }))
  )), [form.productOverrides, selectedOrganizations])
  const selectedProductOverrideCount = selectedProductOverrides.length
  const baselineOrganizations = useMemo(() => {
    if (!editing) return []
    if (baseline.scope !== 'selected') return organizations
    return organizations.filter((organization) => baseline.selectedOrganizationIds.has(organization.id))
  }, [baseline, editing, organizations])
  const baselineProductOverrideCount = Object.keys(baseline.productOverrides).length
  const scopeLabel = (scope) => SCOPE_OPTIONS.find((option) => option.value === scope)?.label || scope
  const organizationSummary = (items) => items.map((organization) => organization.name).join(', ') || 'None'
  const currentCommandCenter = editing && baseline.commandCenterAccess ? 'Enabled' : 'Not enabled'
  const proposedCommandCenter = commandCenterEligible && form.commandCenterAccess ? 'Enabled' : 'Not enabled'
  const baselineOrganizationIds = new Set(baselineOrganizations.map((organization) => organization.id))
  const proposedOrganizationIds = new Set(selectedOrganizations.map((organization) => organization.id))
  const organizationRoleChanges = organizations
    .filter((organization) => baselineOrganizationIds.has(organization.id) || proposedOrganizationIds.has(organization.id))
    .map((organization) => {
      const hasCurrentAccess = editing && baselineOrganizationIds.has(organization.id)
      const hasProposedAccess = proposedOrganizationIds.has(organization.id)
      const current = hasCurrentAccess
        ? formatRoleLabel(baseline.roles[organization.id] || baseline.defaultRole)
        : 'No access'
      const proposed = hasProposedAccess
        ? formatRoleLabel(form.roles[organization.id] || form.defaultRole)
        : 'No access'
      return {
        key: `organization-role-${organization.id}`,
        label: `${organization.name} role`,
        current,
        proposed,
        impact: !hasCurrentAccess
          ? `Grants ${proposed} access to ${organization.name}.`
          : !hasProposedAccess
            ? `Removes access to ${organization.name}.`
            : 'Changes the product actions inherited from this organization role.',
      }
    })
    .filter((item) => !editing || item.current !== item.proposed)
  const proposedProductOverrides = new Map(selectedProductOverrides.map(({ organization, product, actions }) => [
    productOverrideKey(organization.id, product.key),
    actions,
  ]))
  const productActionChanges = [...new Set([
    ...Object.keys(baseline.productOverrides),
    ...proposedProductOverrides.keys(),
  ])].map((key) => {
    const [organizationId, productKey] = key.split('|')
    const organization = organizations.find((item) => item.id === organizationId)
    const product = organization?.products?.find((item) => item.key === productKey)
    const hasCurrentOverride = editing && Object.hasOwn(baseline.productOverrides, key)
    const hasProposedOverride = proposedProductOverrides.has(key)
    const currentActions = hasCurrentOverride ? baseline.productOverrides[key] : null
    const proposedActions = hasProposedOverride ? proposedProductOverrides.get(key) : null
    return {
      key: `product-actions-${key}`,
      label: `${organization?.name || organizationId} · ${product?.name || productKey} actions`,
      current: hasCurrentOverride ? formatProductActions(currentActions) : 'Role defaults',
      proposed: hasProposedOverride ? formatProductActions(proposedActions) : 'Role defaults',
      impact: !hasCurrentOverride
        ? 'Custom actions will replace the organization role defaults for this product.'
        : !hasProposedOverride
          ? 'This product will inherit actions from the organization role.'
          : 'Updates the custom action set for this product.',
      changed: hasCurrentOverride !== hasProposedOverride || !productActionsMatch(currentActions, proposedActions),
    }
  }).filter((item) => !editing || item.changed)
  const accessChanges = [
    {
      key: 'scope',
      label: 'Access scope',
      current: editing ? scopeLabel(baseline.scope) : 'No access',
      proposed: scopeLabel(form.scope),
      impact: form.scope === 'all'
        ? 'Automatically includes organizations created later.'
        : form.scope === 'all_current'
          ? 'Includes every organization that exists now.'
          : 'Limits access to the organizations named below.',
    },
    {
      key: 'default-role',
      label: 'Default role',
      current: editing ? formatRoleLabel(baseline.defaultRole) : 'No role',
      proposed: formatRoleLabel(form.defaultRole),
      impact: 'Sets the default product actions for included organizations.',
    },
    {
      key: 'organizations',
      label: 'Organizations',
      current: editing ? organizationSummary(baselineOrganizations) : 'None',
      proposed: organizationSummary(selectedOrganizations),
      impact: `${selectedOrganizations.length} organization${selectedOrganizations.length === 1 ? '' : 's'} will be in scope.`,
    },
    ...organizationRoleChanges,
    {
      key: 'product-overrides',
      label: 'Product overrides',
      current: editing ? baselineProductOverrideCount : 0,
      proposed: selectedProductOverrideCount,
      impact: selectedProductOverrideCount
        ? 'Custom actions replace the role defaults for the listed products.'
        : 'Product actions inherit from organization roles.',
    },
    ...productActionChanges,
    {
      key: 'command-center',
      label: 'Command Center',
      current: currentCommandCenter,
      proposed: proposedCommandCenter,
      impact: proposedCommandCenter === 'Enabled'
        ? 'Allows cross-organization Command Center work.'
        : 'Cross-organization Command Center work remains unavailable.',
    },
  ].filter((item) => !editing || String(item.current) !== String(item.proposed))

  const updateForm = (updates) => setForm((current) => ({ ...current, ...updates }))
  const toggleOrganization = (organizationId) => {
    setForm((current) => {
      const selectedOrganizationIds = new Set(current.selectedOrganizationIds)
      if (selectedOrganizationIds.has(organizationId)) selectedOrganizationIds.delete(organizationId)
      else selectedOrganizationIds.add(organizationId)
      return { ...current, selectedOrganizationIds }
    })
  }
  const updateOrganizationRole = (organizationId, role) => {
    setForm((current) => ({ ...current, roles: { ...current.roles, [organizationId]: role } }))
  }
  const toggleProductOverride = (organization, product) => {
    const key = productOverrideKey(organization.id, product.key)
    setForm((current) => {
      const productOverrides = { ...current.productOverrides }
      if (Object.hasOwn(productOverrides, key)) {
        delete productOverrides[key]
      } else {
        const role = current.roles[organization.id] || current.defaultRole
        productOverrides[key] = [...(ROLE_PRODUCT_ACTIONS[role] || ['view'])]
      }
      return { ...current, productOverrides }
    })
  }
  const toggleProductAction = (organizationId, productKey, action) => {
    const key = productOverrideKey(organizationId, productKey)
    setForm((current) => {
      const actions = new Set(current.productOverrides[key] || [])
      if (actions.has(action)) {
        if (action === 'view') actions.clear()
        else actions.delete(action)
      } else {
        actions.add(action)
        if (action !== 'view') actions.add('view')
      }
      return {
        ...current,
        productOverrides: {
          ...current.productOverrides,
          [key]: PRODUCT_ACTIONS.map(({ value }) => value).filter((value) => actions.has(value)),
        },
      }
    })
  }


  const payload = () => {
    const selectedIds = form.scope === 'selected'
      ? form.selectedOrganizationIds
      : new Set(organizations.map((organization) => organization.id))
    const assignments = organizations
      .filter((organization) => selectedIds.has(organization.id))
      .map((organization) => ({
        organization_id: organization.id,
        role: form.roles[organization.id] || form.defaultRole,
      }))
      .filter((assignment) => form.scope === 'selected' || assignment.role !== form.defaultRole)

    const productOverrides = selectedProductOverrides.map(({ organization, product, actions }) => ({
      organization_id: organization.id,
      product_key: product.key,
      actions,
    }))

    const access = {
      scope: form.scope,
      default_role: form.defaultRole,
      organizations: assignments,
      product_overrides: productOverrides,
      command_center_access: commandCenterEligible && form.commandCenterAccess,
    }
    if (editing) return access
    return {
      email: form.email.trim(),
      display_name: form.displayName.trim(),
      access,
    }
  }

  const continueWorkflow = () => {
    if (!canContinue) return
    if (step === 'review') {
      onSubmit(payload())
      return
    }
    setStep(STEPS[currentStepIndex + 1].value)
  }
  const goBack = () => {
    if (currentStepIndex === 0) return
    setStep(STEPS[currentStepIndex - 1].value)
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={currentStepIndex === 0 ? onClose : goBack} disabled={isSubmitting}>
        {currentStepIndex === 0 ? 'Cancel' : 'Back'}
      </Button>
      <Button onClick={continueWorkflow} disabled={!canContinue} loading={isSubmitting}>
        {step === 'review' ? (editing ? 'Save access' : 'Send invite') : 'Continue'}
      </Button>
    </>
  )

  return (
    <Dialog
      open
      size="large"
      title={editing ? `Access for ${user.display_name || user.email}` : 'Add Wager team member'}
      description="Organization roles determine the products and actions available in Studio."
      onClose={() => {
        if (!isSubmitting) onClose()
      }}
      dismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
      footer={footer}
    >
      <div className={styles.workflow}>
        <Stepper
          steps={STEPS.map((item, index) => ({ ...item, complete: index < currentStepIndex }))}
          currentStep={step}
          label="User access setup"
        />

        {step === 'account' ? (
          <FormLayout>
            <FormLayoutRow>
              <Field label="Email" required={!editing}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(event) => updateForm({ email: event.target.value })}
                  disabled={editing}
                  autoComplete="email"
                  required={!editing}
                />
              </Field>
              <Field label="Display name" required={!editing}>
                <Input
                  value={form.displayName}
                  onChange={(event) => updateForm({ displayName: event.target.value })}
                  disabled={editing}
                  autoComplete="name"
                  required={!editing}
                />
              </Field>
            </FormLayoutRow>
            {editing ? (
              <Alert variant="info">Account details stay unchanged. This workflow updates organization access only.</Alert>
            ) : null}
          </FormLayout>
        ) : null}

        {step === 'access' ? (
          <div className={styles.accessStep}>
            <fieldset className={styles.scopeOptions}>
              <legend>Access scope</legend>
              {SCOPE_OPTIONS.map((option) => (
                <Radio
                  key={option.value}
                  name="access-scope"
                  value={option.value}
                  checked={form.scope === option.value}
                  onChange={() => updateForm({ scope: option.value })}
                  label={option.label}
                  description={option.description}
                />
              ))}
            </fieldset>

            <Field label="Default organization role" help="This role applies unless an organization override is selected below.">
              <Select value={form.defaultRole} onChange={(event) => updateForm({ defaultRole: event.target.value })}>
                {ASSIGNABLE_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
              </Select>
            </Field>

            <Switch
              label="Command Center access"
              aria-label="Command Center access"
              description={commandCenterEligible
                ? 'Allow this internal admin to work across organizations in Command Center.'
                : 'Requires Internal admin access in at least one organization.'}
              checked={commandCenterEligible && form.commandCenterAccess}
              disabled={!commandCenterEligible}
              onChange={(checked) => updateForm({ commandCenterAccess: checked })}
            />

            <section className={styles.organizationSection} aria-labelledby="organization-access-title">
              <div className={styles.sectionHeading}>
                <div>
                  <h3 id="organization-access-title">Organizations</h3>
                  <p>{form.scope === 'selected' ? 'Choose access and set any role exceptions.' : 'All organizations are included. Change a row only to override the default role.'}</p>
                </div>
                <StatusBadge status="default">{selectedOrganizations.length} selected</StatusBadge>
              </div>
              <div className={styles.organizationList}>
                {organizations.map((organization) => {
                  const selected = form.scope !== 'selected' || form.selectedOrganizationIds.has(organization.id)
                  return (
                    <div key={organization.id} className={styles.organizationRow}>
                      <Checkbox
                        label={organization.name}
                        description={organization.slug}
                        checked={selected}
                        disabled={form.scope !== 'selected'}
                        onChange={() => toggleOrganization(organization.id)}
                      />
                      <Select
                        aria-label={`Role for ${organization.name}`}
                        value={form.roles[organization.id] || form.defaultRole}
                        onChange={(event) => updateOrganizationRole(organization.id, event.target.value)}
                        disabled={!selected}
                        size="compact"
                      >
                        {ASSIGNABLE_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                      </Select>
                    </div>
                  )
                })}
              </div>
            </section>

            <AdvancedDisclosure label="Advanced product action overrides">
              <div className={styles.productOverrides}>
                <p>Roles provide the default actions. Add an override only when one product needs different access.</p>
                {selectedOrganizations.map((organization) => (
                  organization.products?.length ? (
                    <section key={organization.id} className={styles.productOrganization}>
                      <h4>{organization.name}</h4>
                      {organization.products.map((product) => {
                        const key = productOverrideKey(organization.id, product.key)
                        const overridden = Object.hasOwn(form.productOverrides, key)
                        const role = form.roles[organization.id] || form.defaultRole
                        const inheritedActions = ROLE_PRODUCT_ACTIONS[role] || ['view']
                        const actions = form.productOverrides[key] || inheritedActions
                        return (
                          <div key={product.key} className={styles.productOverride}>
                            <Switch
                              label={product.name}
                              description={overridden ? 'Custom actions' : `Inherited from ${formatRoleLabel(role)}`}
                              checked={overridden}
                              onChange={() => toggleProductOverride(organization, product)}
                              aria-label={`Override ${product.name} actions for ${organization.name}`}
                            />
                            {overridden ? (
                              <div className={styles.productActions}>
                                {PRODUCT_ACTIONS.map((action) => (
                                  <Checkbox
                                    key={action.value}
                                    label={action.label}
                                    aria-label={`${action.label} ${product.name} access for ${organization.name}`}
                                    checked={actions.includes(action.value)}
                                    onChange={() => toggleProductAction(organization.id, product.key, action.value)}
                                  />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        )
                      })}
                    </section>
                  ) : null
                ))}
              </div>
            </AdvancedDisclosure>
          </div>
        ) : null}

        {step === 'review' ? (
          <DecisionReview
            title="Review access"
            description="Confirm the exact organization scope and role changes before continuing."
            contextItems={[
              { key: 'user', label: 'User', value: form.displayName || user?.display_name || form.email },
              { key: 'organization-count', label: 'Organizations in scope', value: selectedOrganizations.length },
              {
                key: 'role-assignments',
                label: 'Organization roles',
                value: selectedOrganizations.map((organization) => (
                  `${organization.name}: ${formatRoleLabel(form.roles[organization.id] || form.defaultRole)}`
                )).join('; '),
              },
              ...(selectedProductOverrides.length ? [{
                key: 'overrides',
                label: 'Custom product actions',
                value: selectedProductOverrides.map(({ organization, product, actions }) => (
                  `${organization.name} · ${product.name}: ${formatProductActions(actions)}`
                )).join('; '),
              }] : []),
            ]}
            changeItems={accessChanges}
            changeTitle={editing ? 'Access changes' : 'Access to grant'}
            consequence={form.scope === 'all'
              ? `This user will automatically receive ${formatRoleLabel(form.defaultRole)} access to organizations created later.`
              : editing
                ? 'Saving replaces the current organization-access policy with the values above.'
                : 'Sending the invite grants access after the recipient activates the account.'}
            error={error ? error.response?.data?.error || error.message || 'Access could not be saved' : null}
          />
        ) : null}

        {error && step !== 'review' ? <Alert variant="error">{error.response?.data?.error || error.message || 'Access could not be saved'}</Alert> : null}
      </div>
    </Dialog>
  )
}

export default UserAccessDialog
